import { jsPDF } from "jspdf";

const GOLD = [201, 162, 75];
const BLACK = [23, 19, 10];
const DARKTEXT = [34, 34, 34];
const MUTED = [110, 110, 110];
const GREEN = [46, 125, 60];
const RED = [178, 58, 46];

const money = (n) => "$" + Number(n || 0).toLocaleString();

// Generates a branded one-page investment deal sheet PDF and triggers download.
export function generateDealPdf(deal) {
  const { doc, filename } = buildDealPdf(deal);
  doc.save(filename);
}

// Builds the deal-sheet PDF and returns { base64, filename } for emailing.
export function getDealPdfBase64(deal) {
  const { doc, filename } = buildDealPdf(deal);
  const uri = doc.output("datauristring");
  const base64 = uri.substring(uri.indexOf(",") + 1);
  return { base64, filename };
}

function buildDealPdf(deal) {
  const d = deal || {};
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const M = 16; // margin
  let y = 0;

  const rentalType = d.rentalType || "Long-Term Rental";
  const isSTR = rentalType === "Short-Term Rental";

  // ---- Header band ----
  doc.setFillColor(...BLACK);
  doc.rect(0, 0, W, 30, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 30, W, 1.6, "F");

  doc.setFont("times", "bold");
  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  doc.text("DSCR", M, 17);
  const dscrW = doc.getTextWidth("DSCR");
  doc.setTextColor(...GOLD);
  doc.text("Daily", M + dscrW + 1.5, 17);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(210, 210, 210);
  doc.text("INVESTMENT DEAL SHEET", M, 24);

  doc.setTextColor(...GOLD);
  doc.setFontSize(8);
  doc.text(new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }), W - M, 17, { align: "right" });
  doc.setTextColor(200, 200, 200);
  doc.text("deals@dscrdaily.com", W - M, 23, { align: "right" });

  y = 45;

  // ---- Address / title ----
  doc.setFont("times", "bold");
  doc.setFontSize(19);
  doc.setTextColor(...BLACK);
  const addr = d.address || "Investment Property";
  const addrLines = doc.splitTextToSize(addr, W - 2 * M);
  doc.text(addrLines, M, y);
  y += addrLines.length * 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...MUTED);
  const sub = [d.city, d.market].filter(Boolean).join("  ·  ");
  if (sub) { doc.text(sub, M, y); y += 6; }

  // ---- Tag pills ----
  y += 2;
  const pills = [rentalType, `DSCR ${d.dscr ?? "—"}`, d.badge].filter(Boolean);
  let px = M;
  doc.setFontSize(8);
  pills.forEach((p) => {
    const pw = doc.getTextWidth(p) + 7;
    doc.setDrawColor(...GOLD);
    doc.setFillColor(250, 246, 236);
    doc.roundedRect(px, y - 4, pw, 6.5, 1.2, 1.2, "FD");
    doc.setTextColor(...BLACK);
    doc.text(p, px + 3.5, y);
    px += pw + 3;
  });
  y += 10;

  // ---- Key metrics grid (3 cols x 2 rows) ----
  const cf = Number(d.cashflow || 0);
  const metrics = [
    [isSTR ? "Est. Monthly STR Revenue" : "Monthly Rent", money(d.rent), DARKTEXT],
    ["List Price", money(d.price), DARKTEXT],
    ["Monthly Payment (PITIA)", money(d.payment), DARKTEXT],
    ["Est. Monthly Cash Flow", (cf >= 0 ? "+" : "-") + "$" + Math.abs(cf).toLocaleString(), cf >= 0 ? GREEN : RED],
    ["DSCR Ratio", String(d.dscr ?? "—"), (Number(d.dscr) >= 1 ? GREEN : RED)],
    ["Financing", d.ltv || "—", DARKTEXT],
  ];

  const colW = (W - 2 * M - 8) / 3;
  const cardH = 24;
  metrics.forEach((mtr, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const cx = M + col * (colW + 4);
    const cy = y + row * (cardH + 4);
    doc.setDrawColor(225, 225, 225);
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(cx, cy, colW, cardH, 1.5, 1.5, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    const labelLines = doc.splitTextToSize(mtr[0].toUpperCase(), colW - 6);
    doc.text(labelLines, cx + 3, cy + 6);
    doc.setFont("times", "bold");
    const val = String(mtr[1]);
    // financing string can be long -> smaller
    const isLong = val.length > 16;
    doc.setFontSize(isLong ? 8.5 : 15);
    doc.setTextColor(...mtr[2]);
    const valLines = doc.splitTextToSize(val, colW - 6);
    doc.text(valLines, cx + 3, cy + (isLong ? 15 : 18));
  });
  y += 2 * cardH + 4 + 10;

  // ---- Property details row ----
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.5);
  doc.line(M, y, W - M, y);
  y += 7;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK);
  doc.text("PROPERTY DETAILS", M, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...DARKTEXT);
  const details = [
    ["Beds", d.beds || "—"],
    ["Baths", d.baths || "—"],
    ["Sq Ft", d.sqft ? Number(d.sqft).toLocaleString() : "—"],
    ["Strategy", isSTR ? "Short-Term Rental" : "Long-Term Rental"],
    ["Type", d.type || "—"],
  ];
  details.forEach(([k, v], i) => {
    const dx = M + (i % 3) * ((W - 2 * M) / 3);
    const dy = y + Math.floor(i / 3) * 8;
    doc.setTextColor(...MUTED);
    doc.text(`${k}:`, dx, dy);
    doc.setTextColor(...DARKTEXT);
    doc.text(String(v), dx + doc.getTextWidth(`${k}: `) + 1, dy);
  });
  y += Math.ceil(details.length / 3) * 8 + 8;

  // ---- Disclaimer ----
  doc.setFillColor(248, 245, 238);
  const discText =
    "These are gross, rough numbers. More due diligence will have to be done by the buyer. " +
    "This is only to show the potential based off public data and confirmed rents from listing agents/sellers. " +
    "DSCR Daily is an education & deal-intelligence platform, not a lender. All figures are estimates and not a commitment to lend or a guarantee of returns.";
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  const discLines = doc.splitTextToSize(discText, W - 2 * M - 8);
  const discH = discLines.length * 3.6 + 8;
  doc.roundedRect(M, y, W - 2 * M, discH, 1.5, 1.5, "F");
  doc.setTextColor(...MUTED);
  doc.text(discLines, M + 4, y + 6);
  y += discH + 10;

  // ---- Contact footer ----
  const footY = 280;
  doc.setFillColor(...BLACK);
  doc.rect(0, footY - 6, W, 297 - (footY - 6), "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, footY - 6, W, 1.2, "F");
  doc.setFont("times", "bold");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text("DSCR", M, footY + 2);
  const fw = doc.getTextWidth("DSCR");
  doc.setTextColor(...GOLD);
  doc.text("Daily", M + fw + 1, footY + 2);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(220, 220, 220);
  doc.text("deals@dscrdaily.com   ·   Direct: 571-277-9452", W - M, footY + 1, { align: "right" });

  const safe = (addr || "deal").replace(/[^a-z0-9]+/gi, "-").slice(0, 40);
  return { doc, filename: `DSCRDaily-Deal-${safe}.pdf` };
}
